/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Alert, Button, ConfirmModal, PageContainer, PageHeader, Toast } from '@/components/ui'
import { useApi } from '@/hooks'
import useToast from '@/hooks/useToast'
import { insightName } from '@/insights/insightCatalogue'
import logger from '@/utils/logger'

import { ModeSelector, type BulkMode } from './ModeSelector'
import { SelectedUsersSummary } from './SelectedUsersSummary'

import { LoadingPanel } from '../LoadingPanel'
import {
  APP_GRANT_LABELS,
  PermissionsEditor,
  UnsavedChangesNote,
  buildUsage,
  disabledInUse,
  normaliseValue,
  useCompanyProfile,
  usePermissionsForm,
  usersAndDashboardsLabel,
  type AppGrant,
  type DisabledSet,
  type PermissionsValue,
  type UsageIndex,
} from '../permissions'
import type { AdminUserSummary } from '../types'

const log = logger({ category: 'AdminBulkPermissionsPage' })

interface BulkState {
  uuids?: string[]
}

export interface BulkAccessBody {
  mode: BulkMode
  uuids: string[]
  grants: AppGrant[]
  insights: string[]
}

interface BulkAccessResult {
  updated: string[]
  missing: string[]
}

const EMPTY: PermissionsValue = { grants: [], insights: [] }

/** What the operation would take away from someone who has it on a dashboard. */
const takenAway = (mode: BulkMode, value: PermissionsValue, usage: UsageIndex): DisabledSet => {
  if (mode === 'add') return { grants: [], insights: [] }
  if (mode === 'remove') {
    return {
      grants: value.grants.filter((grant) => usage.inUse(grant)),
      insights: value.insights.filter((slug) => usage.inUse(slug)),
    }
  }
  // Replace: anything in use that is left unchecked stops working.
  return disabledInUse({ grants: [], insights: [...usage.slugs] }, value, usage)
}

const plural = (n: number, noun: string) => `${String(n)} ${noun}${n === 1 ? '' : 's'}`

/** The one-line summary of what Apply will do, by mode. */
const modeSentence = (mode: BulkMode, value: PermissionsValue): string => {
  if (mode === 'replace') return 'All permissions will be replaced.'
  const what = `${plural(value.grants.length, 'app')} and ${plural(value.insights.length, 'insight')}`
  return mode === 'add' ? `${what} will be added.` : `${what} will be removed.`
}

interface BulkConfirmBodyProps {
  count: number
  mode: BulkMode
  value: PermissionsValue
  usage: UsageIndex
}

const BulkConfirmBody: React.FC<BulkConfirmBodyProps> = ({ count, mode, value, usage }) => {
  const taken = takenAway(mode, value, usage)
  const lines = [
    ...taken.grants.map((grant) => ({ key: grant, name: APP_GRANT_LABELS[grant] })),
    ...taken.insights.map((slug) => ({ key: slug, name: insightName(slug) })),
  ]
  return (
    <div data-testid="bulk-confirm">
      <p className="text-sm">{plural(count, 'user')} will be updated.</p>
      <p className="mt-1 text-sm">{modeSentence(mode, value)}</p>
      {lines.length > 0 && (
        <>
          <p className="mt-3 mb-1 text-sm">The following are in use on a dashboard and will stop working:</p>
          <ul className="list-disc pl-5 text-sm">
            {lines.map((line) => (
              <li key={line.key}>
                {line.name} ({usersAndDashboardsLabel(usage.forSlug(line.key))})
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

/**
 * One permissions edit applied to every user picked on the users list. The
 * selection arrives via router state; a refresh loses it and lands back on the list.
 */
const AdminBulkPermissionsPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { api } = useApi()
  const { toast, show, showToast, hideToast } = useToast()
  const uuids = useMemo(() => (location.state as BulkState | null)?.uuids ?? [], [location.state])

  const [users, setUsers] = useState<AdminUserSummary[] | null>(null)
  const [loadError, setLoadError] = useState('')
  const [mode, setMode] = useState<BulkMode>('replace')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [confirming, setConfirming] = useState(false)

  // The editor always starts empty: the checked set IS the operation, in every mode.
  const { value, setValue, isDirty, reset } = usePermissionsForm<PermissionsValue>({
    baseline: EMPTY,
    normalise: normaliseValue,
  })
  const form = value ?? EMPTY

  useEffect(() => {
    if (uuids.length === 0) return
    let cancelled = false
    const load = async () => {
      try {
        // Only the selected users: the full list pulls every dashboard summary.
        const params = new URLSearchParams()
        for (const uuid of uuids) params.append('uuids', uuid)
        const response = await api.get<AdminUserSummary[]>({
          path: `/app/admin/users?${params.toString()}`,
          origin: 'AdminBulkPermissionsPage#load',
        })
        if (cancelled) return
        if (response.error) {
          setLoadError(response.message || 'Could not load users')
          return
        }
        setUsers((response.data ?? []).filter((user) => uuids.includes(user.uid)))
      } catch (ex) {
        log.error(ex)
        if (!cancelled) setLoadError('Could not load users')
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [api, uuids])

  const selected = useMemo(() => users ?? [], [users])
  // One shared company caps like the single-user page; a mix leaves it to each company's own cap.
  const companyIds = useMemo(() => [...new Set(selected.map((user) => user.companyId))], [selected])
  const sharedCompanyId = companyIds.length === 1 && companyIds[0] !== null ? companyIds[0] : undefined
  const mixed = selected.length > 0 && sharedCompanyId === undefined
  const { cap } = useCompanyProfile(sharedCompanyId)
  const usage = useMemo(() => buildUsage(selected), [selected])

  const changeMode = (next: BulkMode) => {
    setMode(next)
    reset()
    setSaveError('')
  }

  const save = useCallback(async () => {
    setIsSaving(true)
    setSaveError('')
    try {
      const response = await api.put<BulkAccessResult, BulkAccessBody>({
        path: '/app/admin/users/access',
        origin: 'AdminBulkPermissionsPage#save',
        body: { mode, uuids, grants: form.grants, insights: form.insights },
      })
      if (response.error || !response.data) {
        setSaveError(response.message || 'Could not save')
        return
      }
      const { updated, missing } = response.data
      showToast({
        header: `Updated ${String(updated.length)} ${updated.length === 1 ? 'user' : 'users'}${
          missing.length > 0 ? `, ${String(missing.length)} not found` : ''
        }`,
        body: 'Changes apply at each user’s next sign-in or token refresh.',
        position: 'end',
        success: true,
      })
      navigate('/admin/users')
    } catch (ex) {
      log.error(ex)
      setSaveError('Could not save')
    } finally {
      setIsSaving(false)
    }
  }, [api, form, mode, navigate, showToast, uuids])

  const handleSave = () => setConfirming(true)
  const handleConfirm = () => {
    setConfirming(false)
    void save()
  }

  if (uuids.length === 0) {
    return <Navigate to="/admin/users" replace />
  }

  const count = selected.length
  const subtitle = users === null ? undefined : `${String(count)} ${count === 1 ? 'user' : 'users'} selected`

  return (
    <PageContainer>
      <Link to="/admin/users" className="link link-hover mb-2 inline-block text-sm">
        ← Users
      </Link>
      <PageHeader title="Set permissions" subtitle={subtitle} />

      {loadError && <Alert message={loadError} type="error" />}

      {users === null && !loadError ? (
        <LoadingPanel label="Loading users…" />
      ) : (
        <section className="card bg-base-100 shadow">
          <div className="card-body">
            <SelectedUsersSummary users={selected} />

            <ModeSelector value={mode} onChange={changeMode} disabled={isSaving} />

            {mixed && (
              <div data-testid="mixed-company-note">
                <Alert
                  type="info"
                  message="Company permissions override individual user permissions"
                  description="These users belong to different companies, so each company's own profile still caps what they can use."
                />
              </div>
            )}

            <PermissionsEditor
              value={form}
              onChange={(next) => setValue(next)}
              cap={mode === 'remove' ? null : cap}
              usage={usage}
              inUseLabel={usersAndDashboardsLabel}
              disabled={isSaving}
            />

            {saveError && <Alert message={saveError} type="error" />}

            <div className="card-actions mt-4 justify-end">
              <Button handleClick={() => navigate('/admin/users')} isDisabled={isSaving} variation="ghost">
                Cancel
              </Button>
              <Button handleClick={handleSave} isDisabled={!isDirty || isSaving || count === 0} variation="primary">
                {isSaving ? (
                  <>
                    <span className="loading loading-spinner loading-xs" />
                    Saving…
                  </>
                ) : (
                  `Apply to ${String(count)} ${count === 1 ? 'user' : 'users'}`
                )}
              </Button>
            </div>
            <UnsavedChangesNote show={isDirty} />

            {/* Always confirm: this writes to every selected user at once. The shared Modal keeps
                its body mounted while closed, so only mount it while there is a question. */}
            {confirming && (
              <ConfirmModal
                show
                title={`Apply to ${plural(count, 'user')}?`}
                variation="warning"
                confirmLabel="Apply"
                cancelLabel="Keep editing"
                isLoading={isSaving}
                onConfirm={handleConfirm}
                onCancel={() => setConfirming(false)}
                body={<BulkConfirmBody count={count} mode={mode} value={form} usage={usage} />}
              />
            )}
          </div>
        </section>
      )}
      <Toast
        show={show}
        onClose={hideToast}
        header={toast.header}
        body={toast.body}
        position={toast.position}
        success={toast.success}
        info={toast.info}
        warning={toast.warning}
      />
    </PageContainer>
  )
}

export default AdminBulkPermissionsPage
